/**
 * PlaylistTileHoverBodyV6 — Hover body for V6.
 *
 * Same layout as V1's hover body, but the `__strip` slot runs the
 * **infinity carousel animation** from V1's hero instead of a static row.
 * The animation is intentionally slower (24 s vs V1's 12 s) and the cards
 * are smaller (~38 % of strip width vs V1 hero's 55 %) so visually there
 * is more empty space between cards as they cycle through center.
 *
 * Layout:
 *   1. Playlist name (avenir-medium, 13/18, weight 800, $body)
 *   2. Meta line (11/16, $body-light)
 *   3. Animated `__strip` — full thumbnail array cycles continuously
 *   4. Bullet list of preview titles (limit 5), 30 ms stagger fade-in
 *   5. "+N more" line if applicable
 *   6. Teal "View Playlist →" CTA pill
 */

import React from 'react'
import StackedCards from '../../../../Icon/icons/StackedCards/index'

// 36 s = 3× slower than V1's 12 s hero animation, 1.5× slower than the
// previous 24 s pass. Combined with the keyframe's wider center-hold
// window (0–12 %), each card lingers visibly at the front before
// drifting off.
const STRIP_ANIMATION_SECONDS = 36
// Cap the strip to ~8 thumbnails so the per-card spacing
// (`duration / N` ≈ 4.5 s) is wider than the center-hold window
// (≈ 4.3 s of 36 s) — that way only one card occupies the front at a
// time, with a brief handoff between adjacent cards.
const MAX_STRIP_THUMBNAILS = 8
const MAX_VISIBLE_TITLES = 5

interface PlaylistTileHoverBodyV6Props {
  playlistId: string
  playlistName: string
  metaLine: string
  thumbnails: string[]
  titles: string[]
  itemCount: number | null
}

function PlaylistTileHoverBodyV6 ({
  playlistId,
  playlistName,
  metaLine,
  thumbnails,
  titles,
  itemCount,
}: PlaylistTileHoverBodyV6Props): React.ReactElement {
  const shownTitles = titles.slice(0, MAX_VISIBLE_TITLES)
  const remaining = (itemCount ?? 0) - shownTitles.length

  // Spread cards evenly around the cycle by negative animation delays —
  // identical pattern to V1's hero. With duration = STRIP_ANIMATION_SECONDS
  // and N cards, each card's delay = -(i * duration / N) seconds so the
  // cycle is "primed" and the cards are already distributed when the
  // panel reveals.
  const stripThumbnails = thumbnails.slice(0, MAX_STRIP_THUMBNAILS)
  const stripCount = stripThumbnails.length
  const strip = stripThumbnails.map((url, i) => {
    const delay = stripCount > 0
      ? `-${(i * STRIP_ANIMATION_SECONDS) / stripCount}s`
      : '0s'
    return (
      <div
        key={`${url}-${i}`}
        className="playlist-search-tile-v6-hover__strip-card"
        style={{ animationDelay: delay }}
      >
        <img
          src={url}
          alt=""
          loading="lazy"
          className="playlist-search-tile-v6-hover__strip-img"
        />
      </div>
    )
  })

  return (
    <div className="playlist-search-tile-v6-hover">
      <span className="playlist-search-tile-v6-hover__name">{playlistName}</span>
      {metaLine && (
        <span className="playlist-search-tile-v6-hover__meta">{metaLine}</span>
      )}

      <div className="playlist-search-tile-v6-hover__strip">
        {stripCount > 0 ? strip : (
          <div className="playlist-search-tile-v6-hover__strip-placeholder">
            <StackedCards size="32px" color="rgba(0, 0, 0, 0.25)" />
          </div>
        )}
      </div>

      {shownTitles.length > 0 && (
        <ul className="playlist-search-tile-v6-hover__titles">
          {shownTitles.map((title, i) => (
            <li
              key={`${title}-${i}`}
              className="playlist-search-tile-v6-hover__title"
            >
              {title}
            </li>
          ))}
        </ul>
      )}

      {remaining > 0 && (
        <span className="playlist-search-tile-v6-hover__more">
          + {remaining} more
        </span>
      )}

      <a
        href={`/playlist/shared/${playlistId}`}
        className="playlist-search-tile-v6-hover__cta"
      >
        View Playlist &rarr;
      </a>
    </div>
  )
}

export default PlaylistTileHoverBodyV6
